import { useState } from "react";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import anchorContainer from "../../container/anchor.container";
import {
  CloseIcon,
  DeleteIcon,
  EditOutlinedIcon,
  ExitIcon,
  ShareLinkIcon,
} from "../../shared/icon";
import VSNButton from "../../shared/VSNButton";

const useStyles = makeStyles((theme) => ({
  MenuWrapper: {
    "&.MuiButtonBase-root": {
      position: "absolute",
      top: 12,
      right: 12,
      backgroundColor: "#4244504D",
      borderRadius: 5,
      backdropFilter: "blur(4px)",
      "& .MuiSvgIcon-root": {
        color: "var(--white)",
      },
    },
  },
  ListMenuWrapper: {
    "& .MuiSvgIcon-root": {
      [theme.breakpoints.down("md")]: {
        width: 14,
      },
    },
  },
  popupIcon: {
    height: 24,
    width: 24,
    borderRadius: "100%",
    backgroundColor: "var(--white)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  deleteIcon: {
    "& svg": {
      height: 14,
    },
  },

  modalHead: {
    "&.MuiStack-root": {
      padding: "20px 26px",
      borderBottom: "1px solid #EAEDF0",
      "& h4": {
        fontWeight: 700,
        fontSize: 24,
        padding: 0,
        lineHeight: "31px",
        color: "var(--blackGray)",
        maxWidth: "95%",
        textAlign: "left",
        [theme.breakpoints.down("md")]: {
          marginBottom: 12,
          fontSize: 22,
          lineHeight: "25px",
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: 18,
        },
      },
      "& .MuiIconButton-root": {
        backgroundColor: "var(--primaryLighter)",
        height: 25,
        width: 25,
        "& svg": {
          margin: 0,
          marginBottom: 0,
          color: "var(--primary)",
        },
        "&:hover": {
          backgroundColor: "var(--primaryGradientLighter)",
        },
      },
    },
  },
  container: {
    position: "static",
    textAlign: "center",
    "& .MuiDialogActions-root": {
      justifyContent: "center",
      backgroundColor: "#F6F6FD",
      padding: "24px 40px 18px",
      borderRadius: 5,
      [theme.breakpoints.down("sm")]: {
        padding: "18px 20px",
      },
    },
    "& .MuiDialogContent-root": {
      padding: "0 60px",
      marginBottom: 20,
      [theme.breakpoints.down("sm")]: {
        padding: "0 16px",
      },
      "& .info": {
        marginBottom: 10,
        marginTop: 24,
        [theme.breakpoints.down("md")]: {
          marginBottom: 12,
          width: 250,
        },
      },
    },

    "& p": {
      fontSize: 14,
      lineHeight: "21px",
      color: "var(--gray)",
      "& span": {
        fontWeight: 700,
        marginRight: 4,
      },
    },
  },
  cancelButton: {
    "&.MuiButtonBase-root": {
      background: "transparent",
      color: "var(--primary)",
    },
  },
  attentionButton: {
    "& .MuiButtonBase-root": {
      padding: [[8, 37]],
      borderRadius: 10,
      width: "auto",
      "& .content": {
        fontSize: 16,
      },
      [theme.breakpoints.down("md")]: {
        padding: [[8, 28]],
      },
    },
  },
  logoutButton: {
    "&.MuiButtonBase-root": {
      color: "var(--white)",
    },
  },
}));

const TeamActions = ({
  id,
  handleExitTeam,
  handleShareLink,
  isListView,
  canEditTeam,
  handleEditTeam,
  canExitTeam,
  canDeleteTeam,
  handleDeleteTeam,
  admins,
  moduleName,
  isLoading,
}) => {
  const { open, anchorEl, setAnchorEl, handleClose } = anchorContainer();
  const classes = useStyles();

  const [openConfirm, setOpenConfirm] = useState(false);

  const handleOpenConfirm = (e) => {
    e.stopPropagation();
    handleClose();
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleConfirmDelete = () => {
    handleDeleteTeam(id);
    if (isLoading) {
      setOpenConfirm(false);
    }
  };

  return (
    <>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        size="small"
        sx={
          isListView
            ? {
                "&.MuiButtonBase-root": {
                  position: "relative",
                  top: "unset",
                  right: "unset",
                  padding: { md: "0" },
                  background: { md: "none", xs: "#7477871A" },
                  borderRadius: 1,
                  color: { md: "var(--blackGray)", xs: "var(--gray)" },
                  transform: {
                    md: "rotate(0)",
                    xs: "rotate(-90deg)",
                  },
                  height: { md: "fit-content", xs: "24px" },
                  width: { md: "fit-content", xs: "24px" },
                },
              }
            : { ml: 2 }
        }
        aria-controls={open ? `menu` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        className={isListView ? classes.ListMenuWrapper : classes.MenuWrapper}
      >
        {isListView ? <MoreVertIcon /> : <MoreHorizIcon />}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id="menu"
        open={open}
        onClose={(e) => {
          e.stopPropagation();
          handleClose(e);
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleClose(e);
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.10)",
            background: "var(--white)",
            backdropFilter: "blur(3px)",
            borderRadius: "8px",
            padding: "5px",
            "& .MuiList-root ": {
              padding: 0,
              "& .MuiMenuItem-root": {
                padding: "8px",
                color: "var(--blackGray)",
                "& span": {
                  marginLeft: "8px",
                  fontSize: "14px",
                },
              },
            },
          },
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
      >
        {canEditTeam && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleEditTeam();
              handleClose();
            }}
          >
            <Box className={classes.popupIcon}>
              <EditOutlinedIcon />
            </Box>
            <span>{`Edit ${moduleName ?? ""}`}</span>
          </MenuItem>
        )}
        {canExitTeam && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
              return handleExitTeam(id, admins);
            }}
          >
            <Box className={classes.popupIcon}>
              <ExitIcon />
            </Box>
            <span>{`Exit ${moduleName ?? ""}`}</span>
          </MenuItem>
        )}
        {canDeleteTeam && (
          <MenuItem onClick={handleOpenConfirm}>
            <Box className={`${classes.popupIcon} ${classes.deleteIcon}`}>
              <DeleteIcon />
            </Box>
            <span>{`Delete ${moduleName ?? ""}`}</span>
          </MenuItem>
        )}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
            return handleShareLink(e);
          }}
        >
          <Box className={classes.popupIcon}>
            <ShareLinkIcon />
          </Box>
          <span>Share Link</span>
        </MenuItem>
      </Menu>

      <Dialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={classes.container}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className={classes.modalHead}
          >
            <DialogTitle id="draggable-dialog-title" component="h4">
              Are You Sure?
            </DialogTitle>
            <IconButton aria-label="close" onClick={handleCloseConfirm}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <DialogContent>
            <InfoRoundedIcon
              sx={{
                color: "var(--primary)",
                height: "94px",
                width: "94px",
              }}
              className="info"
              fontSize="55px"
            />
            <DialogContentText
              className={classes.note}
              component="p"
              sx={{ mb: { xs: "30px", md: 3 } }}
            >
              Are you sure you want to delete this team?
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Stack
              width="100%"
              direction={{ xs: "column", xts: "row" }}
              gap={{ md: 3, xs: 2 }}
              className={classes.attentionButton}
              justifyContent="center"
            >
              <VSNButton
                handleClick={handleCloseConfirm}
                className={classes.cancelButton}
                variant="outlined"
              >
                Cancel
              </VSNButton>
              <VSNButton
                handleClick={handleConfirmDelete}
                className={classes.logoutButton}
                isLoading={isLoading}
              >
                Delete
              </VSNButton>
            </Stack>
          </DialogActions>
        </div>
      </Dialog>
    </>
  );
};

export default TeamActions;
